import { useState, useEffect } from "react";
import { Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { subscribe } from "@/lib/api";
import { getRecaptchaToken, recaptchaEnabled } from "@/lib/recaptcha";

function getRefFromUrl() {
    if (typeof window === "undefined") return null;
    const u = new URL(window.location.href);
    return u.searchParams.get("ref");
}

export default function SubscribeForm({ variant = "footer", className = "" }) {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    const [ref, setRef] = useState(null);

    useEffect(() => {
        setRef(getRefFromUrl());
        // Pre-warm reCAPTCHA on mount
        if (recaptchaEnabled) getRecaptchaToken("page_view").catch(() => {});
    }, []);

    async function onSubmit(e) {
        e.preventDefault();
        if (!email) return;
        setBusy(true);
        try {
            const captcha_token = recaptchaEnabled ? await getRecaptchaToken("subscribe") : null;
            const res = await subscribe({
                email,
                name: name || undefined,
                ref: ref || undefined,
                captcha_token: captcha_token || undefined,
            });
            toast.success(res.message || "Subscribed!");
            if (res.referral_code) {
                const link = `${window.location.origin}/?ref=${res.referral_code}`;
                navigator.clipboard?.writeText(link).catch(() => {});
                toast.message("Your share link was copied to clipboard!", { description: link });
            }
            setEmail("");
            setName("");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Couldn't subscribe — please try again.";
            toast.error(typeof msg === "string" ? msg : "Couldn't subscribe.");
        } finally {
            setBusy(false);
        }
    }

    if (variant === "hero") {
        return (
            <form
                onSubmit={onSubmit}
                className={`card-sticker bg-white p-6 sm:p-8 ${className}`}
                data-testid="subscribe-form-hero"
            >
                {ref && (
                    <div className="mb-4 px-4 py-2 bg-mint border-2 border-ink rounded-full inline-flex items-center gap-2 text-sm font-bold" data-testid="referred-banner">
                        🎁 A friend invited you! When you join, they get one step closer to the Family Pack.
                    </div>
                )}
                <div className="flex items-start gap-4 mb-4">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-sun border-2 border-ink shrink-0">
                        <Mail className="w-6 h-6 text-ink" strokeWidth={2.5} />
                    </span>
                    <div>
                        <h3 className="font-heading text-2xl font-bold">Get the daily devotional</h3>
                        <p className="text-ink/70 text-sm mt-1">
                            One short Bible lesson (ESV) + a kindness checklist, delivered each morning. Free.
                        </p>
                    </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                    <input
                        type="text"
                        placeholder="Kid's first name (optional)"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="border-2 border-ink rounded-full px-4 py-3 font-semibold focus:outline-none focus:ring-4 focus:ring-sky"
                        data-testid="subscribe-name"
                    />
                    <input
                        type="email"
                        required
                        placeholder="Parent's email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="border-2 border-ink rounded-full px-4 py-3 font-semibold focus:outline-none focus:ring-4 focus:ring-sky"
                        data-testid="subscribe-email"
                    />
                </div>
                <button
                    type="submit"
                    disabled={busy}
                    className="btn-sticker bg-coral text-white w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="subscribe-submit"
                >
                    <Sparkles className="w-4 h-4 mr-2" strokeWidth={2.5} />
                    {busy ? "Sending..." : "Subscribe"}
                </button>
                {recaptchaEnabled && (
                    <p className="text-xs text-ink/50 mt-3" data-testid="recaptcha-notice">
                        Protected by reCAPTCHA. Google's{" "}
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline">Privacy</a>{" "}&amp;{" "}
                        <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline">Terms</a> apply.
                    </p>
                )}
            </form>
        );
    }

    return (
        <form onSubmit={onSubmit} className="space-y-2" data-testid="subscribe-form-footer">
            <p className="font-heading font-bold mb-1">Daily devotional</p>
            <p className="text-sm text-ink/70 mb-2">A free ESV Bible lesson in your inbox each morning.</p>
            <div className="flex flex-col sm:flex-row gap-2">
                <input
                    type="email"
                    required
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 border-2 border-ink rounded-full px-4 py-2 text-sm font-semibold focus:outline-none focus:ring-4 focus:ring-sky"
                    data-testid="footer-subscribe-email"
                />
                <button
                    type="submit"
                    disabled={busy}
                    className="btn-sticker bg-sun text-ink text-sm disabled:opacity-60"
                    data-testid="footer-subscribe-submit"
                >
                    {busy ? "..." : "Join"}
                </button>
            </div>
        </form>
    );
}
